import type {
  HelpCenterValue,
  SalesforceCommunityPageType,
  SalesforceLanguageCode,
} from 'bundles/common/utils/salesforceUrlBuilder';

export const zendeskHelpCenterSubdomains = {
  LEARNER: 'learner',
  BUSINESS: 'business',
  PARTNER: 'partner',
} as const;

export type ZendeskHelpCenterSubdomainValue =
  (typeof zendeskHelpCenterSubdomains)[keyof typeof zendeskHelpCenterSubdomains];
export type ZendeskHelpCenterSubdomainKey = keyof typeof zendeskHelpCenterSubdomains;

// The following are only the page types that might be present in zendeskToSalesforceArticleMappings.json
export const zendeskPageTypes = {
  ARTICLE: 'article',
  CATEGORY: 'category',
  SECTION: 'section',
} as const;

export type ZendeskPageTypeValue = (typeof zendeskPageTypes)[keyof typeof zendeskPageTypes];
export type ZendeskPageTypeKey = keyof typeof zendeskPageTypes;

export const pageTypes = {
  ...zendeskPageTypes,
  HOME: 'home', // e.g. learner help center home page
  WEBFORM: 'webform',
  NOTFOUND: 'notfound',
  COMMUNITY_THREAD: 'community_thread',
  COMMUNITY_GROUP: 'community_group',
  COMMUNITY_GROUP_FALLBACK: 'community_group_fallback',
  COMMUNITY_NOTFOUND: 'community_notfound',
  // we'll use undefined for other/unknown page types
} as const;

export type PageTypeValue = (typeof pageTypes)[keyof typeof pageTypes];
export type PageTypeKey = keyof typeof pageTypes;

export type ZendeskHelpCenterPageMappings = Record<ZendeskPageTypeValue, Record<string, string>>;

export type ZendeskToSalesforcePageMappingsCollection = Record<
  ZendeskHelpCenterSubdomainValue,
  ZendeskHelpCenterPageMappings
>;

/**
 * Note: The 'id' language code was never actually supported by our Zendesk help centers, but going forward we may want to have /hc/id/ in article permalinks if there is a use case to specifically link to an Indonesian article.
 */
export type RequestedZendeskLanguageCode = '' | 'en' | 'ar' | 'es' | 'id';

export const inSidedPageTypes = {
  THREAD: 'thread',
  GROUP: 'group',
} as const;

export type InsidedPageTypeValue = (typeof inSidedPageTypes)[keyof typeof inSidedPageTypes];
export type InsidedPageTypeKey = keyof typeof inSidedPageTypes;

// This type is likely only useful for migration/redirection instead of generic SF URL building
// There are other fields in the migration result file that we're keeping for records or potential future use, but we're only using the following fields for redirection (see inSidedToSalesforceCommunityMappings.json)
export type SalesforceCommunityPageInfo = {
  sfId: 'string';
  type?: SalesforceCommunityPageType; // optional because SF group pages don't have a type, and we can assume its TextPost if missing
};

/**
 * key string is the inSided thread ID or forum/group ID
 */
export type InsidedPageMappings = Record<string, SalesforceCommunityPageInfo>;

export type InsidedToSalesforceMappingsCollection = Record<InsidedPageTypeValue, InsidedPageMappings>;

export type RedirectStatusCode = 301 | 302 | 303 | 307 | 308;

// All fields besides location and statusCode are optional because they're only used for tracking
// Also, in some cases the value won't be applicable or will be unknown, so we'll use undefined for those cases
export type RedirectInfo = {
  location: string;
  statusCode: RedirectStatusCode;
  helpCenter?: HelpCenterValue;
  pageType?: PageTypeValue;
  pageId?: string;
  /** language='' differs from language='en_US', because in the case of '' we let Salesforce decide the language and don't know what it will be */
  language?: SalesforceLanguageCode | '';
};
